<template>
  <div v-if="!disabled" v-on-click-outside="closeMenu" class="select-interval">
    <button
      v-focus
      tabindex="0"
      class="select-box"
      :aria-expanded="isMenuOpen"
      aria-haspopup="listbox"
      :aria-label="intervalLabel"
      @click="toggleMenu()"
      @keydown.escape="closeMenu"
    >
      <span>{{ intervalLabel }}</span>
      <i
        :class="
          isMenuOpen ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'
        "
      ></i>
    </button>

    <div
      v-show="isMenuOpen"
      role="listbox"
      class="menu"
      @keydown.tab="handleTabKey"
      @keydown.escape="closeMenu"
    >
      <!-- preset interval -->
      <TimerangeOptions
        :interval-preset="intervalPreset"
        :start-date="start_date"
        :end-date="end_date"
        @select-time="selectTime"
        @set-start="start_date = $event"
        @set-end="end_date = $event"
      />

      <!-- Reset -->
      <li class="btn-row">
        <button
          v-if="isCustomIntervalValid"
          v-focus
          class="btn-primary btn-apply"
          @click="selectTime(TIME_INTERVALS.FREE)"
        >
          Filtrer la date
        </button>
        <button v-focus class="btn-reset btn-secondary" @click="resetInterval">
          <i class="fa-solid fa-arrow-rotate-left"></i>
          <span class="label">Réinitialiser</span>
        </button>
      </li>
    </div>
  </div>

  <!-- Disabled State -->
  <div v-else class="select-interval disabled">
    <button class="select-box" disabled aria-label="Période désactivée">
      <span>Période</span>
      <i class="fa-solid fa-chevron-down"></i>
    </button>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import dayjs from "dayjs";

import TimerangeOptions from "@follaw-fr/components/general/atoms/TimerangeOptions.vue";
import { vOnClickOutside } from "@vueuse/components";
import { TIME_INTERVALS } from "@follaw-fr/stores/user";
import {
  getEndDateFromPreset,
  getStartDateFromPreset,
} from "@follaw-fr/utils/date.utils";
import { useToggleMenu } from "@follaw-fr/composables/useToggleMenu";

const props = defineProps({
  intervalPreset: {
    type: String,
    required: true,
  },
  intervalStart: {
    type: String,
    default: "",
  },
  intervalEnd: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["setTimeRange"]);
const { isMenuOpen, toggleMenu, closeMenu } = useToggleMenu();

function handleTabKey(event) {
  const focusableElements = Array.from(
    event.currentTarget.querySelectorAll("button, [tabindex='0']")
  );
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  if (event.shiftKey && event.target === firstElement) {
    event.preventDefault();
    closeMenu();
  } else if (!event.shiftKey && event.target === lastElement) {
    event.preventDefault();
    closeMenu();
  }
}

const start_date = ref(
  props.intervalStart?.split("T")[0] ??
    getStartDateFromPreset(props.intervalPreset)
);
const end_date = ref(
  props.intervalEnd?.split("T")[0] ?? getEndDateFromPreset(props.intervalPreset)
);

const isCustomIntervalValid = computed(
  () =>
    !!start_date.value &&
    !!end_date.value &&
    dayjs(start_date.value) < dayjs(end_date.value)
);

const intervalLabel = computed(() => {
  switch (props.intervalPreset) {
    case TIME_INTERVALS.LAST_24H:
      return "< 24h";
    case TIME_INTERVALS.LAST_WEEK:
      return "< 1 semaine";
    case TIME_INTERVALS.LAST_MONTH:
      return "< 1 mois";
    case TIME_INTERVALS.LAST_YEAR:
      return "< 1 an";
    case TIME_INTERVALS.LEGI_15:
      return "XVe legi.";
    case TIME_INTERVALS.LEGI_16:
      return "XVIe legi.";
    case TIME_INTERVALS.LEGI_17:
      return "XVIIe legi.";
    default:
      if (start_date.value && end_date.value) {
        return `${dayjs(start_date.value).format("DD/MM/YY")} - ${dayjs(
          end_date.value
        ).format("DD/MM/YY")}`;
      } else {
        return "Période";
      }
  }
});

function selectTime(option) {
  if (option !== TIME_INTERVALS.FREE) {
    start_date.value = getStartDateFromPreset(option);
    end_date.value = getEndDateFromPreset(option);
  }

  emit("setTimeRange", {
    interval: option,
    start: dayjs(start_date.value).tz("Europe/Paris").format(),
    end: dayjs(end_date.value).tz("Europe/Paris").format(),
  });
  closeMenu();
}

function resetInterval() {
  emit("setTimeRange", {
    interval: TIME_INTERVALS.LAST_24H,
    start: getStartDateFromPreset(TIME_INTERVALS.LAST_24H),
    end: getEndDateFromPreset(TIME_INTERVALS.LAST_24H),
  });
  closeMenu();
}
</script>

<style scoped lang="scss">
.select-interval {
  position: relative;
  -webkit-user-select: none;
  user-select: none;

  /* Input */
  .select-box {
    display: flex;
    align-items: center;
    justify-content: space-between;

    width: fit-content;
    @media (max-width: 1100px) {
      width: 120px;
    }

    height: 30px;
    padding: 4px 15px;
    background: var(--clr-p8);
    border-radius: 15px;
    box-shadow: 0 3px 5px #00000014;

    color: white;
    font-size: v-bind(
      'props.intervalPreset === "free" ? "0.75rem" : "inherit"'
    );

    cursor: pointer;

    .select-arrow {
      margin-left: 5px;
      pointer-events: none;
    }
  }

  &.disabled > .select-box {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Popover Menu */
  .menu {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 30px;
    left: 0;
    box-shadow: 0 1px 6px #0000005d;
    border-radius: 13px;
    width: 280px;
    max-height: 435px;
    padding: 8px;
    margin-top: 16px;
    overflow: hidden;
    color: var(--clr-n8);
    background: white;
  }
}

/* Footer Reset */
.btn-row {
  display: flex;
  justify-content: space-between;
  padding: 0 10px 5px 10px;
  gap: 10px;

  .btn-apply {
    font-size: 0.813rem;
    flex-shrink: 0;
  }

  .btn-reset {
    font-size: 0.9rem;

    .label {
      margin-left: 4px;
    }
  }
}
</style>
